"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { CandlestickChart, ArrowLeft, RefreshCw, LayoutDashboard } from "lucide-react";

interface StockBrokingTopBarProps {
  onResetDemo?: () => void;
  isResetting?: boolean;
}

export function StockBrokingTopBar({ onResetDemo, isResetting = false }: StockBrokingTopBarProps) {
  const pathname = usePathname();
  const isCrm = pathname?.includes("/crm");

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
        {/* Left: brand & back */}
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Link href="/demos" className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors shrink-0">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">All Demos</span>
          </Link>
          <div className="h-4 w-px bg-slate-100 shrink-0" />
          <Link href="/demos/stock-broking" className="flex items-center gap-2.5 group min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform shrink-0">
              <CandlestickChart className="w-5 h-5 text-slate-900" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-900 tracking-tight text-sm sm:text-base truncate">Johnson Securities</span>
                <span className="hidden md:inline text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 font-semibold border border-emerald-500/20">
                  Broking
                </span>
              </div>
              <p className="text-[10px] text-slate-500 hidden sm:block">NSE · BSE · MCX · NSDL Member · SEBI Reg.</p>
            </div>
          </Link>
        </div>

        {/* Center: portal / CRM switch */}
        <div className="flex items-center bg-white border border-slate-200 p-1 rounded-xl shrink-0">
          <Link
            href="/demos/stock-broking"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${!isCrm ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow" : "text-slate-500 hover:text-slate-900"}`}
          >
            <span className="hidden sm:inline">Client </span>Portal
          </Link>
          <Link
            href="/demos/stock-broking/crm"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${isCrm ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow" : "text-slate-500 hover:text-slate-900"}`}
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Broking </span>CRM
          </Link>
        </div>

        {/* Right: reset */}
        <div className="flex items-center gap-2 shrink-0">
          {onResetDemo && (
            <button
              type="button"
              onClick={onResetDemo}
              disabled={isResetting}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-600 transition-colors px-2 py-1 rounded-lg border border-slate-200 hover:border-emerald-500/30 bg-white"
              title="Reset demo data to initial seed"
            >
              <RefreshCw className={`w-3 h-3 ${isResetting ? "animate-spin text-emerald-600" : ""}`} />
              <span className="hidden md:inline">Reset Demo</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
